import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { todayISO } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { F } from "@/pages/Employees";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { CheckCheck, Copy, Save, Search, Trash2, AlertTriangle, LogIn, LogOut, History } from "lucide-react";
import { toast } from "sonner";
import { AttendanceDuplicates, AttendanceMoves } from "@/components/AttendanceDuplicates";

const STATUS = ["Hadir", "Izin", "Sakit", "Alpha", "Libur", "Setengah Hari"];

export default function Attendance() {
  const [employees, setEmployees] = useState([]);
  const [tanggal, setTanggal] = useState(todayISO());
  const [rows, setRows] = useState({});
  const [q, setQ] = useState("");
  const [wp, setWp] = useState({ normal: 8, istirahat: 1, batas: 4 });
  const overLimit = (g) => g.status === "Hadir" && Number(g.jam_lembur || 0) > wp.batas;
  const confirmOver = (list) => {
    if (!list.length) return true;
    return window.confirm(`PERINGATAN: lembur melebihi batas wajar ${wp.batas} jam/hari:\n\n${list.map((x) => `- ${x.nama}: ${x.jam} jam`).join("\n")}\n\nPastikan jam pulang sudah benar. Tetap simpan?`);
  };

  useEffect(() => { api.get("/employees?menu=/attendance").then((r) => setEmployees(r.data.filter((e) => e.aktif === true && !e.merged_into))); }, []);
  useEffect(() => { api.get("/settings").then((r) => setWp({ normal: Number(r.data.jam_kerja_standar || 8), istirahat: Number(r.data.durasi_istirahat || 1), batas: Number(r.data.batas_lembur_harian || 4) })).catch(() => {}); }, []);

  const { user } = useAuth();
  const isManager = ["super_admin", "direktur", "hr"].includes(user?.role);
  const [mandorFilter, setMandorFilter] = useState("all");
  const [conflicts, setConflicts] = useState([]);
  const [lastRows, setLastRows] = useState([]);
  const [dupOpen, setDupOpen] = useState(() => ["super_admin", "direktur", "hr"].includes(user?.role) && new URLSearchParams(window.location.search).get("dobel") === "1");
  const [movesOpen, setMovesOpen] = useState(false);
  const handleConflicts = (data, payload) => {
    const c = data.conflicts || [];
    setConflicts(c);
    setLastRows(payload.filter((p) => c.some((x) => x.employee_id === p.employee_id)));
    if (c.length) toast.error(c.length === 1 ? c[0].message : `${c.length} pegawai ditolak (sudah diabsen di proyek lain / bukan tim Anda)`);
  };
  const forceMove = async () => {
    try {
      const r = await api.post("/attendance/bulk", { tanggal, rows: lastRows, force: true });
      toast.success(`${r.data.saved} absensi dipindah ke proyek aktif pegawai`);
      setConflicts(r.data.conflicts || []); loadExisting();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const pimpinanOpts = employees.filter((e) => e.is_pimpinan);

  const empList = employees.filter((e) => {
    const t = q.toLowerCase();
    const matchText = !t || e.nama.toLowerCase().includes(t) || (e.kode_pegawai || "").toLowerCase().includes(t);
    const matchMandor = mandorFilter === "all" || e.pimpinan_id === mandorFilter;
    return matchText && matchMandor;
  });

  const belumDiabsen = tanggal === todayISO()
    ? employees.filter((e) => !rows[e.id]).length
    : 0;

  const defRow = () => ({ status: "Hadir", jam_masuk: "08:00", jam_pulang: "16:00", jam_lembur: "", tugas_proyek: true, keterangan: "" });
  const get = (eid) => rows[eid] || defRow();
  const setRow = (eid, patch) => setRows((r) => ({ ...r, [eid]: { ...(r[eid] || defRow()), ...patch } }));
  const projOf = (e) => e.project_id || "";

  const hoursBetween = (a, b) => {
    if (!a || !b) return 0;
    const [h1, m1] = a.split(":").map(Number), [h2, m2] = b.split(":").map(Number);
    let t = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (t < 0) t += 1440;
    return t / 60;
  };
  const autoLembur = (masuk, pulang) => {
    if (!masuk || !pulang) return "";
    const ot = hoursBetween(masuk, pulang) - wp.normal;
    return Math.max(0, Math.round(ot * 10) / 10);
  };
  const addHours = (t, h) => {
    if (!t) return "";
    const [hh, mm] = t.split(":").map(Number);
    let total = hh * 60 + mm + Math.round(h * 60);
    total = ((total % 1440) + 1440) % 1440;
    return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
  };

  const loadExisting = async () => {
    const r = await api.get(`/attendance?tanggal=${tanggal}&menu=/attendance`);
    const map = {};
    r.data.forEach((a) => { map[a.employee_id] = { id: a.id, status: a.status, jam_masuk: a.jam_masuk, jam_pulang: a.jam_pulang, jam_lembur: a.jam_lembur, telat: a.telat, menit_telat: a.menit_telat, tugas_proyek: a.tugas_proyek !== false, keterangan: a.keterangan }; });
    setRows(map);
  };
  useEffect(() => { loadExisting(); /* eslint-disable-next-line */ }, [tanggal]);

  const delAtt = async (eid) => {
    const g = rows[eid];
    if (!g?.id) { setRows((r) => { const n = { ...r }; delete n[eid]; return n; }); return; }
    try {
      await api.delete(`/attendance/${g.id}`);
      toast.success("Absensi dihapus");
      setRows((r) => { const n = { ...r }; delete n[eid]; return n; });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setAllHadir = () => {
    const map = {};
    empList.forEach((e) => { map[e.id] = { ...get(e.id), status: "Hadir", jam_masuk: "08:00", jam_pulang: "16:00", jam_lembur: autoLembur("08:00", "16:00") }; });
    setRows((r) => ({ ...r, ...map }));
  };

  const toggleHadir = (eid, on) => {
    if (on) setRow(eid, { status: "Hadir", jam_masuk: "08:00", jam_pulang: "16:00", jam_lembur: autoLembur("08:00", "16:00") });
    else setRow(eid, { status: "Alpha", jam_masuk: "", jam_pulang: "", jam_lembur: "" });
  };

  const copyPrev = async () => {
    try {
      const r = await api.post("/attendance/copy-previous", { tanggal });
      toast.success(`${r.data.copied} baris disalin dari ${r.data.from_date}`);
      loadExisting();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveRow = async (e, label) => {
    const g = get(e.id);
    if (overLimit(g) && !confirmOver([{ nama: e.nama, jam: g.jam_lembur }])) return;
    try {
      const payload = [{
        employee_id: e.id, project_id: projOf(e), status: g.status || "Hadir",
        jam_masuk: g.jam_masuk, jam_pulang: g.jam_pulang,
        jam_lembur: g.jam_lembur === "" ? null : Number(g.jam_lembur),
        tugas_proyek: g.tugas_proyek !== false, keterangan: g.keterangan,
      }];
      const res = await api.post("/attendance/bulk", { tanggal, rows: payload });
      handleConflicts(res.data, payload);
      if (!res.data.saved) return;
      const r = await api.get(`/attendance?tanggal=${tanggal}&employee_id=${e.id}`);
      const a = r.data[0];
      if (a) setRow(e.id, { id: a.id, status: a.status, jam_masuk: a.jam_masuk, jam_pulang: a.jam_pulang, jam_lembur: a.jam_lembur, telat: a.telat, menit_telat: a.menit_telat });
      toast.success(`${label} tersimpan`);
    } catch (err) { toast.error(apiErr(err)); }
  };

  const saveAll = async () => {
    if (!confirmOver(empList.filter((e) => overLimit(get(e.id))).map((e) => ({ nama: e.nama, jam: get(e.id).jam_lembur })))) return;
    const payload = empList.map((e) => {
      const g = get(e.id);
      return { employee_id: e.id, project_id: projOf(e), status: g.status, jam_masuk: g.jam_masuk, jam_pulang: g.jam_pulang,
        jam_lembur: g.jam_lembur === "" ? null : Number(g.jam_lembur), tugas_proyek: g.tugas_proyek !== false, keterangan: g.keterangan };
    });
    try {
      const r = await api.post("/attendance/bulk", { tanggal, rows: payload });
      toast.success(`${r.data.saved} absensi tersimpan`);
      handleConflicts(r.data, payload);
      loadExisting();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Absensi Cepat" subtitle="Isi jam datang lalu Simpan; saat pulang isi jam pulang lalu Simpan — lembur terisi otomatis" />
      {belumDiabsen > 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="att-reminder">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <span><b>{belumDiabsen}</b> pegawai belum diabsen hari ini. Mohon lengkapi absensi anak buah Anda sebelum tutup hari.</span>
        </div>
      )}
      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <F label="Tanggal"><Input type="date" value={tanggal} data-testid="att-date" onChange={(e) => setTanggal(e.target.value)} /></F>
          <F label="Cari Pegawai">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input className="pl-9" placeholder="Nama / kode" value={q} data-testid="att-search" onChange={(e) => setQ(e.target.value)} />
            </div>
          </F>
          {isManager && <F label="Filter Mandor/Pimpinan">
            <Select value={mandorFilter} onValueChange={setMandorFilter}>
              <SelectTrigger data-testid="att-mandor-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Mandor</SelectItem>
                {pimpinanOpts.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={setAllHadir} data-testid="att-set-hadir"><CheckCheck className="h-4 w-4 mr-1" /> Centang Semua Hadir</Button>
            <Button variant="outline" onClick={copyPrev} data-testid="att-copy-prev"><Copy className="h-4 w-4 mr-1" /> Salin Kemarin</Button>
            {isManager && <Button variant="outline" className="border-rose-200 text-rose-700" onClick={() => setDupOpen(true)} data-testid="att-dup-btn"><AlertTriangle className="h-4 w-4 mr-1" /> Cek Absensi Dobel</Button>}
            {isManager && <Button variant="outline" onClick={() => setMovesOpen(true)} data-testid="att-moves-btn"><History className="h-4 w-4 mr-1" /> Riwayat Pindah Proyek</Button>}
          </div>
        </div>
      </Card>

      {conflicts.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800" data-testid="att-conflicts">
          <div className="flex items-start gap-2"><AlertTriangle className="h-5 w-5 shrink-0" />
            <div className="flex-1"><b>Absensi berikut ditolak (tidak disimpan):</b>
              <ul className="mt-1 list-disc pl-5">{conflicts.map((c) => <li key={c.employee_id}>{c.message}</li>)}</ul>
            </div>
            <button className="text-xs underline" onClick={() => setConflicts([])} data-testid="att-conflicts-close">Tutup</button>
          </div>
          {isManager && lastRows.length > 0 && (
            <Button size="sm" className="mt-2 bg-rose-700 hover:bg-rose-800" onClick={forceMove} data-testid="att-force-move">Pindahkan ke proyek aktif pegawai (timpa data lama)</Button>
          )}
        </div>
      )}
      <AttendanceDuplicates open={dupOpen} onClose={() => setDupOpen(false)} onResolved={loadExisting} />
      <AttendanceMoves open={movesOpen} onClose={() => setMovesOpen(false)} />

      <div className="overflow-auto scroll-thin max-h-[70vh] rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="att-table">
          <thead><tr className="border-b text-xs font-bold uppercase tracking-wider text-slate-500">
            <th className="sticky top-0 z-10 bg-slate-100 px-3 py-3 w-14 text-center">Hadir</th>
            <th className="sticky top-0 z-10 bg-slate-100 px-3 py-3 text-left min-w-[160px]">Nama</th>
            <th className="sticky top-0 z-10 bg-slate-100 px-3 py-3 text-left">Status</th>
            <th className="sticky top-0 z-10 bg-slate-100 px-3 py-3 text-center">Proyek?</th>
            <th className="sticky top-0 z-10 bg-slate-100 px-3 py-3 text-left">Jam Masuk</th>
            <th className="sticky top-0 z-10 bg-slate-100 px-3 py-3 text-left">Jam Pulang</th>
            <th className="sticky top-0 z-10 bg-slate-100 px-3 py-3 text-left">Jam Lembur</th>
            <th className="sticky top-0 z-10 bg-slate-100 px-3 py-3 text-left">Aksi</th>
            <th className="sticky top-0 z-10 bg-slate-100 px-3 py-3 text-left">Keterangan</th>
          </tr></thead>
          <tbody>
            {empList.length === 0 ? (
              <tr><td colSpan={9} className="text-center text-slate-400 py-8">Tidak ada pegawai aktif.</td></tr>
            ) : empList.map((e, i) => {
              const g = get(e.id);
              const tint = g.id && g.jam_masuk && g.jam_pulang ? "bg-emerald-50/60" : (g.id && g.jam_masuk ? "bg-amber-50/50" : "");
              return (
                <tr key={e.id} className={`border-b border-slate-100 hover:bg-slate-50 ${tint}`} data-testid={`att-row-${i}`}>
                  <td className="px-3 py-2 text-center"><input type="checkbox" className="h-4 w-4 accent-emerald-600" checked={g.status === "Hadir"} data-testid={`att-hadir-${i}`} onChange={(ev) => toggleHadir(e.id, ev.target.checked)} /></td>
                  <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">{e.nama}<div className="text-xs text-slate-400">{e.kode_pegawai} · {e.jabatan || "-"}</div>
                    <div className="mt-1 flex gap-1">
                      {g.id && g.jam_masuk && <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700" data-testid={`att-badge-datang-${i}`}>● DATANG {g.jam_masuk}</span>}
                      {g.id && g.jam_pulang && <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold text-blue-700" data-testid={`att-badge-pulang-${i}`}>● PULANG {g.jam_pulang}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <Select value={g.status} onValueChange={(v) => setRow(e.id, { status: v })}>
                      <SelectTrigger className="h-9 w-32" data-testid={`att-status-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2 text-center"><input type="checkbox" className="h-4 w-4 accent-blue-700" checked={g.tugas_proyek !== false} data-testid={`att-proyek-${i}`} onChange={(ev) => setRow(e.id, { tugas_proyek: ev.target.checked })} /></td>
                  <td className="px-3 py-2"><Input type="time" className="h-9 w-28" value={g.jam_masuk} data-testid={`att-masuk-${i}`} onChange={(ev) => { const m = ev.target.value; const p = addHours(m, wp.normal); setRow(e.id, { jam_masuk: m, jam_pulang: p, jam_lembur: autoLembur(m, p) }); }} />
                    {g.telat && <span className="mt-0.5 inline-block rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-700" data-testid={`att-telat-${i}`}>TELAT {g.menit_telat}m</span>}
                  </td>
                  <td className="px-3 py-2"><Input type="time" className="h-9 w-28" value={g.jam_pulang} data-testid={`att-pulang-${i}`} onChange={(ev) => setRow(e.id, { jam_pulang: ev.target.value, jam_lembur: autoLembur(g.jam_masuk, ev.target.value) })} /></td>
                  <td className="px-3 py-2"><Input type="number" step="0.5" placeholder="auto" className={`h-9 w-24 ${overLimit(g) ? "border-rose-500 bg-rose-50 text-rose-700 font-bold" : ""}`} value={g.jam_lembur} data-testid={`att-lembur-${i}`} onChange={(ev) => setRow(e.id, { jam_lembur: ev.target.value })} />
                    {overLimit(g) && <div className="mt-0.5 text-[11px] font-semibold text-rose-600" data-testid={`att-lembur-warn-${i}`}>&gt; batas {wp.batas} jam</div>}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" className="h-8 px-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid={`att-save-datang-${i}`} onClick={() => saveRow(e, "Jam datang")}><LogIn className="h-3.5 w-3.5 mr-1" /> Save Datang</Button>
                      <Button size="sm" variant="outline" className="h-8 px-2 text-blue-700 border-blue-200 hover:bg-blue-50" data-testid={`att-save-pulang-${i}`} onClick={() => saveRow(e, "Jam pulang")}><LogOut className="h-3.5 w-3.5 mr-1" /> Save Pulang</Button>
                      {g.id && <Button size="sm" variant="outline" className="h-8 px-2 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`att-del-${i}`} onClick={() => delAtt(e.id)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                    </div>
                  </td>
                  <td className="px-3 py-2"><Input className="h-9" value={g.keterangan} onChange={(ev) => setRow(e.id, { keterangan: ev.target.value })} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end">
        <Button className="bg-blue-800 hover:bg-blue-900" onClick={saveAll} data-testid="att-save-btn"><Save className="h-4 w-4 mr-1" /> Simpan Semua Absensi</Button>
      </div>
      <p className="text-xs text-slate-400">Isi <b>Jam Masuk</b> lalu klik <b>Save Datang</b> pada baris pegawai. Ketika pulang, isi <b>Jam Pulang</b> lalu klik <b>Save Pulang</b> — <b>Jam Lembur</b> terisi otomatis dari selisih jam (dikurangi istirahat & jam kerja standar) dan tetap bisa diubah manual. Tombol <b>Simpan Semua Absensi</b> di bawah menyimpan seluruh baris sekaligus. Proyek diambil otomatis dari data pegawai bila ada.</p>
    </div>
  );
}
